// SCR-STAFF-870 (جزئیات کالا — AP-INV-01): تعریف، واحدها و معادل‌ها (AC-INV-002.1)، کدها؛ بچ‌ها با وضعیت انقضا و FEFO (US-INV-030، BR-INV-031)؛ ثبت
// بچ دستی یا با اسکن GS1 (AC-INV-003.1) که شماره بچ و انقضا را پر می‌کند؛ ورود از صفحه فهرست با ?scan= فرم بچ را پیش‌پر باز می‌کند.
// AP-INT-04 (US-INV-080): تب «شناسه‌های رهگیری» برای کالای مشمول سامانه ردیابی — وضعیت اصالت و گزارش مصرف، استعلام دوباره
// و صف‌کردن دوباره گزارش نشسته (AC-INV-080.1).
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { unwrap, type InvBatchView, type InvItemUnit, type InvItemView, type InvTrackingUidView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, DataTable, DetailPage, ErrorState, faDigits, JalaliDate, SampleBadge, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { WriteNotice } from '../settings/common';
import { BatchDialog } from './BatchDialog';
import { ItemDialog } from './ItemDialog';
import { batchTone, conversionText, convertQuantity, inventoryKeys } from './inventoryCommon';
import { SCR_STAFF_870 } from './ItemsPage';

function Field({ labelKey, children }: { labelKey: string; children: ReactNode }) {
  const { t } = useTranslation('inv');
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {t(labelKey)}
      </Typography>
      <Typography component="div">{children}</Typography>
    </Box>
  );
}

export function ItemDetailPage() {
  const { id = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('inv.item.manage');
  const batchWrite = useWriteAccess('inv.batch.manage');
  const receiptWrite = useWriteAccess('inv.receipt.manage');
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('details');
  const [editing, setEditing] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [batchDialog, setBatchDialog] = useState<{ scan?: string } | null>(null);
  const [activeOnly, setActiveOnly] = useState(false);

  const item = useQuery({
    queryKey: inventoryKeys.item(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items/{id}', { params: { path: { id } } })),
  });
  const batches = useQuery({
    queryKey: [...inventoryKeys.batches(session?.tenantId, id), activeOnly],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items/{id}/batches', { params: { path: { id }, query: { activeOnly } } })),
    enabled: Boolean(item.data?.tracksBatch),
  });
  const trackingKey = ['inventory', session?.tenantId, 'tracking-uids', id] as const;
  const trackingUids = useQuery({
    queryKey: trackingKey,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/tracking-uids', { params: { query: { itemId: id } } })),
    enabled: Boolean(item.data?.tracksUid),
  });
  const refreshTracking = () => void queryClient.invalidateQueries({ queryKey: trackingKey });
  const verifyUid = useMutation({
    mutationFn: (row: InvTrackingUidView) =>
      unwrap(tenantApi.POST('/api/v1/inventory/tracking-uids/{id}:verify', { params: { path: { id: row.id } } })),
    onSettled: refreshTracking,
  });
  const requeueUid = useMutation({
    mutationFn: (row: InvTrackingUidView) =>
      unwrap(tenantApi.POST('/api/v1/inventory/tracking-uids/{id}:requeue-report', { params: { path: { id: row.id } } })),
    onSettled: refreshTracking,
  });

  // ورود از اسکن صفحه فهرست: فرم بچ با متن اسکن.
  const scan = searchParams.get('scan');
  useEffect(() => {
    if (scan && item.data?.tracksBatch && batchWrite.canWrite) {
      setTab('batches');
      setBatchDialog({ scan });
      setSearchParams({}, { replace: true });
    }
  }, [scan, item.data?.tracksBatch, batchWrite.canWrite, setSearchParams]);

  const toggle = useMutation({
    mutationFn: (current: InvItemView) =>
      unwrap(
        current.isActive
          ? tenantApi.POST('/api/v1/inventory/items/{id}:deactivate', { params: { path: { id: current.id } } })
          : tenantApi.POST('/api/v1/inventory/items/{id}:activate', { params: { path: { id: current.id } } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.items(session?.tenantId) });
      setToggling(false);
    },
  });

  const view = item.data;
  const unitLabel = (unit: InvItemUnit) => t(`inv:unit.${unit}`);

  const batchColumns: DataTableColumn<InvBatchView>[] = [
    { id: 'number', headerKey: 'inv:batches.columns.number', cell: (row) => <bdi dir="ltr" data-testid={`batch-row-${row.batchNumber}`}>{row.batchNumber}</bdi> },
    { id: 'expiry', headerKey: 'inv:batches.columns.expiry', cell: (row) => (row.expiryDate ? <JalaliDate value={row.expiryDate} /> : '—') },
    {
      id: 'days',
      headerKey: 'inv:batches.columns.days',
      cell: (row) =>
        row.daysToExpiry === null || row.daysToExpiry === undefined
          ? '—'
          : Number(row.daysToExpiry) > 0
            ? t('inv:batches.days_left', { value: faDigits(Number(row.daysToExpiry)) })
            : t('inv:batches.expired'),
    },
    { id: 'supplier', headerKey: 'inv:batches.columns.supplier', cell: (row) => row.supplierName ?? '—' },
    { id: 'source', headerKey: 'inv:batches.columns.source', cell: (row) => t(`inv:batch_source.${row.source}`) },
    {
      id: 'status',
      headerKey: 'inv:batches.columns.status',
      cell: (row) => (
        <span data-testid={`batch-status-${row.batchNumber}`} data-status={row.status}>
          <StatusChip tone={batchTone(row.status)} labelKey={`inv:batch_status.${row.status}`} />
        </span>
      ),
    },
  ];

  const details = view && (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
        <Field labelKey="inv:item_form.code">
          <bdi dir="ltr">{view.code}</bdi>
        </Field>
        <Field labelKey="inv:item_form.type">{t(`inv:item_type.${view.type}`)}</Field>
        <Field labelKey="inv:item_form.category">{view.categoryName ?? '—'}</Field>
        <Field labelKey="inv:item_form.brand">{view.brandName ?? '—'}</Field>
        <Field labelKey="inv:item_form.manufacturer">{view.manufacturer ?? '—'}</Field>
        <Field labelKey="inv:item.tracking">
          {[view.tracksBatch && t('inv:items.tracking.batch'), view.tracksExpiry && t('inv:items.tracking.expiry'), view.isMultiUse && t('inv:items.tracking.multi_use')]
            .filter(Boolean)
            .join(' · ') || t('inv:item.no_tracking')}
        </Field>
        <Field labelKey="inv:item_form.base_unit">{unitLabel(view.baseUnit)}</Field>
        <Field labelKey="inv:item_form.purchase_unit">{unitLabel(view.purchaseUnit)}</Field>
        <Field labelKey="inv:item_form.consumption_unit">{unitLabel(view.consumptionUnit)}</Field>
        {view.isMultiUse && (
          <Field labelKey="inv:item_form.beyond_use_hours">
            {view.beyondUseHours === null || view.beyondUseHours === undefined ? t('inv:item.until_expiry') : t('inv:item.hours', { value: faDigits(Number(view.beyondUseHours)) })}
          </Field>
        )}
        <Field labelKey="inv:item_form.gtin">
          <bdi dir="ltr">{view.gtin ?? '—'}</bdi>
        </Field>
        <Field labelKey="inv:item_form.irc">
          <bdi dir="ltr">{view.irc ?? '—'}</bdi>
        </Field>
        <Field labelKey="inv:item_form.barcodes">
          <bdi dir="ltr">{view.barcodes.join(' · ') || '—'}</bdi>
        </Field>
      </Box>
      <Box>
        <Typography variant="subtitle2">{t('inv:item.conversions')}</Typography>
        {view.conversions.length === 0 ? (
          <Typography color="text.secondary">{t('inv:item.no_conversions')}</Typography>
        ) : (
          <Stack component="ul" sx={{ m: 0, ps: 3 }} data-testid="item-conversions">
            {view.conversions.map((conversion) => (
              <li key={conversion.unit}>{conversionText(conversion, view.baseUnit, unitLabel)}</li>
            ))}
          </Stack>
        )}
        {view.purchaseUnit !== view.baseUnit && view.consumptionUnit !== view.baseUnit && (
          <Typography color="text.secondary" data-testid="item-equivalent">
            {t('inv:item.equivalent', {
              purchase: `${faDigits(1)} ${unitLabel(view.purchaseUnit)}`,
              base: `${faDigits(convertQuantity(1, view.purchaseUnit, view.baseUnit, view.baseUnit, view.conversions) ?? 0)} ${unitLabel(view.baseUnit)}`,
              consumption: `${faDigits(convertQuantity(1, view.purchaseUnit, view.consumptionUnit, view.baseUnit, view.conversions) ?? 0)} ${unitLabel(view.consumptionUnit)}`,
            })}
          </Typography>
        )}
      </Box>
      {view.description && <Field labelKey="inv:item_form.description">{view.description}</Field>}
    </Stack>
  );

  const trackingColumns: DataTableColumn<InvTrackingUidView>[] = [
    { id: 'uid', headerKey: 'inv:tracking.uid', cell: (row) => <bdi dir="ltr">{faDigits(row.uid)}</bdi> },
    { id: 'batch', headerKey: 'inv:tracking.batch', cell: (row) => row.batchNumber ?? '—' },
    {
      id: 'status',
      headerKey: 'inv:tracking.status',
      cell: (row) => (
        <span data-testid="tracking-uid-status" data-state={row.status}>
          {t(`inv:tracking.statuses.${row.status}`)}
          {row.verdict ? (
            <Typography variant="caption" display="block" color="text.secondary">
              {t('inv:tracking.verdict')}: {row.verdict}
            </Typography>
          ) : null}
        </span>
      ),
    },
    {
      id: 'report',
      headerKey: 'inv:tracking.report_state',
      cell: (row) => (
        <span data-testid="tracking-uid-report" data-state={row.reportState}>
          {t(`inv:tracking.report_states.${row.reportState}`)}
          {row.lastError ? (
            <Typography variant="caption" display="block" color="text.secondary">
              <bdi dir="ltr">{row.lastError}</bdi>
            </Typography>
          ) : null}
        </span>
      ),
    },
    { id: 'dispensed', headerKey: 'inv:tracking.dispensed_on', cell: (row) => (row.dispensedOn ? <JalaliDate value={row.dispensedOn} /> : '—') },
    {
      id: 'actions',
      headerKey: 'inv:tracking.verify',
      cell: (row) =>
        receiptWrite.canWrite ? (
          <Stack direction="row" spacing={0.5}>
            {row.status !== 'Dispensed' && (
              <Button size="small" onClick={() => verifyUid.mutate(row)} disabled={verifyUid.isPending} data-testid="tracking-uid-verify">
                {t('inv:tracking.verify')}
              </Button>
            )}
            {row.reportState === 'Failed' && (
              <Button size="small" onClick={() => requeueUid.mutate(row)} disabled={requeueUid.isPending} data-testid="tracking-uid-requeue">
                {t('inv:tracking.requeue')}
              </Button>
            )}
          </Stack>
        ) : null,
    },
  ];

  const batchesTab = view && (
    <Stack spacing={2}>
      {!view.tracksBatch ? (
        <Alert severity="info">{t('inv:batches.not_tracked')}</Alert>
      ) : (
        <>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <FormControlLabel
              control={<Switch checked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)} slotProps={{ input: { 'aria-label': t('inv:batches.active_only') } }} />}
              label={t('inv:batches.active_only')}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setBatchDialog({})} disabled={!batchWrite.canWrite || !view.isActive} data-testid="batch-create">
              {t('inv:batches.add')}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('inv:batches.phase1_note')}
          </Typography>
          <DataTable
            captionKey="inv:batches.caption"
            columns={batchColumns}
            rows={batches.data?.items}
            getRowId={(row) => row.id}
            isLoading={batches.isPending}
            error={batches.error}
            onRetry={() => void batches.refetch()}
            emptyTitleKey="inv:batches.empty"
          />
        </>
      )}
    </Stack>
  );

  // AP-INT-04 (US-INV-080): وضعیت اصالت و گزارش مصرف شناسه‌های رهگیری همین کالا؛ استعلام دوباره و صف‌کردن دوباره گزارش.
  const trackingTab = view && (
    <Stack spacing={2}>
      {!view.tracksUid ? (
        <Alert severity="info">{t('inv:tracking.tracks_uid_help')}</Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {t('inv:tracking.subtitle')}
          </Typography>
          {verifyUid.error ? <ErrorState error={verifyUid.error} compact /> : null}
          {requeueUid.error ? <ErrorState error={requeueUid.error} compact /> : null}
          <DataTable
            captionKey="inv:tracking.title"
            columns={trackingColumns}
            rows={trackingUids.data?.items}
            getRowId={(row) => row.id}
            isLoading={trackingUids.isPending}
            error={trackingUids.error}
            onRetry={() => void trackingUids.refetch()}
            emptyTitleKey="inv:tracking.empty"
          />
        </>
      )}
    </Stack>
  );

  return (
    <>
      <DetailPage
        screenId={SCR_STAFF_870}
        titleKey="inv:item.title"
        titleValues={{ name: view?.name ?? '' }}
        subtitleKey="inv:item.subtitle"
        badges={
          view && (
            <Stack direction="row" spacing={1}>
              <StatusChip tone={view.isActive ? 'active' : 'inactive'} labelKey={view.isActive ? 'inv:status.active' : 'inv:status.inactive'} />
              {view.isSample && <SampleBadge />}
            </Stack>
          )
        }
        actions={
          view && (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => setToggling(true)} disabled={!write.canWrite} data-testid="item-toggle-active">
                {t(view.isActive ? 'inv:item.deactivate' : 'inv:item.activate')}
              </Button>
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setEditing(true)} disabled={!write.canWrite} data-testid="item-edit">
                {t('inv:item.edit')}
              </Button>
            </Stack>
          )
        }
        isLoading={item.isPending}
        error={item.error}
        onRetry={() => void item.refetch()}
        tabs={[
          { id: 'details', labelKey: 'inv:item.tabs.details', content: details },
          { id: 'batches', labelKey: 'inv:item.tabs.batches', content: batchesTab },
          { id: 'tracking', labelKey: 'inv:tracking.title', content: trackingTab },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      />
      {editing && view && (
        <ItemDialog
          item={view}
          onClose={() => setEditing(false)}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: inventoryKeys.item(session?.tenantId, id) })}
        />
      )}
      {batchDialog && view && <BatchDialog item={view} initialScan={batchDialog.scan} onClose={() => setBatchDialog(null)} />}
      {view && (
        <ConfirmDialog
          open={toggling}
          titleKey={view.isActive ? 'inv:item.deactivate_title' : 'inv:item.activate_title'}
          messageKey={view.isActive ? 'inv:item.deactivate_message' : 'inv:item.activate_message'}
          messageValues={{ name: view.name }}
          confirmKey={view.isActive ? 'inv:item.deactivate' : 'inv:item.activate'}
          destructive={view.isActive}
          busy={toggle.isPending}
          onConfirm={() => toggle.mutate(view)}
          onCancel={() => setToggling(false)}
          error={toggle.error ?? undefined}
        />
      )}
    </>
  );
}
